import { createApp } from 'vue';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { Tooltip } from 'bootstrap';
import App from './App.vue';
import * as Sentry from '@sentry/vue';
import axios, { HttpStatusCode } from 'axios';

/*
TIP: To get started with clean router change path to @/router/clean.ts.
 */
import router from './router/clean';
import ElementPlus from 'element-plus';
import i18n from '@/core/plugins/i18n';

//imports for app initialization
import ApiService from '@/core/services/ApiService';
import { initApexCharts } from '@/core/plugins/apexcharts';
import { initInlineSvg } from '@/core/plugins/inline-svg';
import { initVeeValidate } from '@/core/plugins/vee-validate';
import { initKtIcon } from '@/core/plugins/keenthemes';
import QrcodeVue from 'qrcode.vue';
import { VueReCaptcha } from 'vue-recaptcha-v3';

import '@/core/plugins/prismjs';

//prime vue
import PrimeVue from 'primevue/config';
import 'primevue/resources/primevue.min.css';
import 'primevue/resources/themes/lara-light-blue/theme.css';

// vue-multiselect
import 'vue-multiselect/dist/vue-multiselect.css';

//dynamic dialog
import DialogService from 'primevue/dialogservice';

// time picker
import VueTimepicker from 'vue3-timepicker';
import 'vue3-timepicker/dist/VueTimepicker.css';

import mitt from 'mitt';
import onlyNumbersDirective from './directives/v-only-numbers';
import onlyEnglishCharsDirective from './directives/v-english-chars';

const emitter = mitt();

const app = createApp(App);

app
	.directive('only-numbers', onlyNumbersDirective)
	.directive('only-english', onlyEnglishCharsDirective);

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

app.use(VueReCaptcha, {
	siteKey: import.meta.env.VITE_APP_RECAPTCHA_SITE_KEY,
	loaderOptions: { useRecaptchaNet: true },
});
app.use(pinia);
app.use(router);
app.use(ElementPlus);

app.component('qrcode-vue', QrcodeVue);
app.component('vue-timepicker', VueTimepicker);

new ApiService().init(app);
initApexCharts(app);
initInlineSvg(app);
initKtIcon(app);
initVeeValidate();

app.use(i18n);

app.use(PrimeVue);
app.use(DialogService);

app.directive('tooltip', (el) => {
	new Tooltip(el);
});

Sentry.init({
	app,
	dsn: import.meta.env.VITE_APP_SENTRY_DSN,
	integrations: [],
	environment: import.meta.env.VITE_APP_SENTRY_ENV,
	ignoreErrors: [
		'NotAllowedError',
		/NotAllowedError:/,
		/The request is not allowed by the user agent/,
	],
	beforeSend(event, hint) {
		const error = hint.originalException;

		if (axios.isAxiosError(error)) {
			if (
				error.response?.status === HttpStatusCode.BadRequest ||
				error.response?.status === HttpStatusCode.Unauthorized ||
				error.response?.status === HttpStatusCode.Forbidden ||
				error.response?.status === HttpStatusCode.Conflict ||
				error.response?.status === HttpStatusCode.InternalServerError ||
				(error.code != undefined &&
					['ECONNABORTED', 'ERR_NETWORK', 'ETIMEDOUT'].includes(error.code))
			) {
				// Don't send this errors to Sentry
				return null;
			}
		}

		return event;
	},
});

// Register Service Worker
if ('serviceWorker' in navigator) {
	window.addEventListener('load', () => {
		navigator.serviceWorker.register('/service-worker.js');
	});
}

app.mount('#app');

export { emitter };
